import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setProgress,
  setTourSettings,
  updateTourProgress,
  removeTourProgress,
  setLoading,
  setError,
  resetState,
} from 'src/redux/slices/tour';
import { useTourContext } from 'src/components/TourProvider';
import useAuth from 'src/hooks/useAuth';
import { getTourLocalCache, setTourLocalCache } from 'src/hooks/tourCache';
import { getDiscriminators } from 'src/utils/permissions';
import isOwnerImpersonating from 'src/utils/adminImpersonation';
import * as tourAPI from 'src/backend/tour';

const writeTourLocalCache = (userId, teamId, progress, settings) => {
  const cached = getTourLocalCache(userId, teamId);

  setTourLocalCache(userId, teamId, {
    progress,
    settings: {
      ...(cached?.settings ?? {}),
      ...(settings ?? {}),
    },
  });
};

export async function persistTourCompletion(
  tourKey,
  lastStepIndex,
  { dispatch, userId, teamId, progress }
) {
  if (!tourKey || !userId || !teamId) return;
  const stepReached =
    typeof lastStepIndex === 'number'
      ? lastStepIndex
      : (progress[tourKey]?.stepReached ?? 0);

  dispatch(
    updateTourProgress({
      tourKey,
      completed: true,
      skipped: false,
      stepReached,
    })
  );

  try {
    await tourAPI.updateProgress(tourKey, { completed: true, stepReached });
    const newProgress = {
      ...progress,
      [tourKey]: { completed: true, skipped: false, stepReached },
    };
    writeTourLocalCache(userId, teamId, newProgress);
  } catch (err) {
    dispatch(setError(err.message));
  }
}

export async function persistTourStepReached(
  tourKey,
  currentStepIndex,
  { dispatch, userId, teamId, progress }
) {
  if (!tourKey || !userId || !teamId) return;
  const stepReached =
    typeof currentStepIndex === 'number'
      ? currentStepIndex
      : (progress[tourKey]?.stepReached ?? 0);

  dispatch(
    updateTourProgress({
      tourKey,
      stepReached,
      completed: false,
      skipped: false,
    })
  );

  try {
    await tourAPI.updateProgress(tourKey, { stepReached, completed: false });
    const newProgress = {
      ...progress,
      [tourKey]: {
        ...progress[tourKey],
        stepReached,
        completed: false,
        skipped: false,
      },
    };
    writeTourLocalCache(userId, teamId, newProgress);
  } catch {
    // Silently fail for step-only updates
  }
}

const useTour = (tourConfig) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const {
    startTour: startDriverTour,
    stopTour,
    isRunning,
    currentTour,
    areToursHidden,
    hideAllToursModalOpen,
  } = useTourContext();
  const { progress, lastFetchedAt } = useSelector((state) => state.tour);
  const tourSettings = useSelector((state) => state.tour.settings);
  const hasInitialized = useRef(false);
  const lastSessionKey = useRef(null);

  const tourKey = tourConfig?.tourKey;
  const userId = user?.id;
  const teamId = user?.team_id;
  const isImpersonating = isOwnerImpersonating(user);
  const sessionKey =
    userId && teamId
      ? `${userId}_${teamId}_${isImpersonating ? 'impersonating' : 'normal'}`
      : null;

  useEffect(() => {
    if (sessionKey === lastSessionKey.current) return;
    lastSessionKey.current = sessionKey;
    hasInitialized.current = false;
    dispatch(resetState());
  }, [sessionKey, dispatch]);

  const discriminators = useMemo(() => {
    return user ? getDiscriminators(user) : null;
  }, [user]);

  const tourProgress = useMemo(() => {
    return tourKey ? progress[tourKey] : null;
  }, [progress, tourKey]);

  const isCompleted = tourProgress?.completed ?? false;
  const isSkipped = tourProgress?.skipped ?? false;
  const stepReached = tourProgress?.stepReached ?? 0;

  const visibleStepCount = useMemo(() => {
    if (!tourConfig?.steps?.length) return 0;
    return tourConfig.steps.filter((step) => {
      if (step.showIf) {
        return step.showIf(discriminators);
      }
      return true;
    }).length;
  }, [tourConfig, discriminators]);

  // Tour has new steps added after the user already completed it
  const hasNewSteps =
    isCompleted && visibleStepCount > 0 && stepReached < visibleStepCount;

  const shouldAutoStart = useMemo(() => {
    if (!tourKey || !tourConfig) return false;
    if (isImpersonating) return false;
    if (lastFetchedAt == null) return false;
    if (!tourSettings.isLoaded) return false;
    if (areToursHidden) return false;
    if (hideAllToursModalOpen) return false;
    if (isSkipped) return false;
    if (isCompleted && !hasNewSteps) return false;
    if (isRunning) return false;

    // Check conditions
    if (tourConfig.conditions && discriminators) {
      const { orgTypes, userTypes, plans } = tourConfig.conditions;

      if (orgTypes && !orgTypes.includes(discriminators.orgType)) {
        return false;
      }
      if (userTypes && !userTypes.includes(discriminators.userType)) {
        return false;
      }
      if (plans && !plans.includes(discriminators.plan)) {
        return false;
      }
    }

    return true;
  }, [
    tourKey,
    tourConfig,
    lastFetchedAt,
    isCompleted,
    isSkipped,
    isRunning,
    areToursHidden,
    hideAllToursModalOpen,
    hasNewSteps,
    discriminators,
    tourSettings.isLoaded,
    isImpersonating,
  ]);

  // Fetch progress on mount
  useEffect(() => {
    if (!userId || !teamId || hasInitialized.current || isImpersonating) return;
    hasInitialized.current = true;

    const fetchProgress = async () => {
      // Check local cache first
      const cached = getTourLocalCache(userId, teamId);
      if (cached) {
        dispatch(
          setTourSettings({
            ...(cached.settings ?? {}),
            isLoaded: true,
          })
        );
        dispatch(setProgress(cached.progress ?? {}));
        return;
      }

      // Fetch from server
      dispatch(setLoading(true));
      try {
        const data = await tourAPI.getAllProgress();
        const progressData = data?.progress ?? {};
        dispatch(
          setTourSettings({
            areToursHidden: data?.settings?.areToursHidden ?? false,
            isLoaded: true,
          })
        );
        dispatch(setProgress(progressData));
        setTourLocalCache(userId, teamId, {
          progress: progressData,
          settings: {
            areToursHidden: data?.settings?.areToursHidden ?? false,
          },
        });
      } catch (err) {
        dispatch(setError(err.message));
      }
    };

    fetchProgress();
  }, [userId, teamId, isImpersonating, dispatch]);

  const onClose = useCallback(
    (currentStepIndex) => {
      if (isImpersonating) return;
      persistTourStepReached(tourKey, currentStepIndex, {
        dispatch,
        userId,
        teamId,
        progress,
      });
    },
    [tourKey, userId, teamId, progress, dispatch, isImpersonating]
  );

  const startTour = useCallback(
    (onComplete, startAtStep, onCloseCallback) => {
      if (isImpersonating) return;
      if (!tourConfig?.steps) return;
      startDriverTour(
        tourConfig.steps,
        tourKey,
        onComplete,
        startAtStep,
        onCloseCallback ?? onClose
      );
    },
    [tourConfig, tourKey, startDriverTour, onClose, isImpersonating]
  );

  const completeTour = useCallback(
    async (lastStepIndex) => {
      if (!tourKey || !userId || !teamId || isImpersonating) return;

      const stepReachedValue =
        typeof lastStepIndex === 'number'
          ? lastStepIndex
          : (progress[tourKey]?.stepReached ?? 0);
      dispatch(
        updateTourProgress({
          tourKey,
          completed: true,
          skipped: false,
          stepReached: stepReachedValue,
        })
      );

      try {
        await tourAPI.updateProgress(tourKey, {
          completed: true,
          stepReached: stepReachedValue,
        });
        // Update cache
        const newProgress = {
          ...progress,
          [tourKey]: {
            completed: true,
            skipped: false,
            stepReached: stepReachedValue,
          },
        };
        writeTourLocalCache(userId, teamId, newProgress);
      } catch (err) {
        dispatch(setError(err.message));
      }
    },
    [tourKey, userId, teamId, progress, dispatch, isImpersonating]
  );

  const skipTour = useCallback(async () => {
    if (!tourKey || !userId || !teamId || isImpersonating) return;

    dispatch(updateTourProgress({ tourKey, completed: false, skipped: true }));
    stopTour();

    try {
      await tourAPI.updateProgress(tourKey, { skipped: true });
      // Update cache
      const newProgress = {
        ...progress,
        [tourKey]: { completed: false, skipped: true, stepReached },
      };
      writeTourLocalCache(userId, teamId, newProgress);
    } catch (err) {
      dispatch(setError(err.message));
    }
  }, [
    tourKey,
    userId,
    teamId,
    progress,
    stepReached,
    stopTour,
    dispatch,
    isImpersonating,
  ]);

  const resetTour = useCallback(async () => {
    if (!tourKey || !userId || !teamId || isImpersonating) return;

    dispatch(removeTourProgress(tourKey));

    try {
      await tourAPI.resetTour(tourKey);
      // Update cache
      const newProgress = { ...progress };
      delete newProgress[tourKey];
      writeTourLocalCache(userId, teamId, newProgress);
    } catch (err) {
      dispatch(setError(err.message));
    }
  }, [tourKey, userId, teamId, progress, dispatch, isImpersonating]);

  const updateStepReached = useCallback(
    async (step) => {
      if (!tourKey || !userId || !teamId || isImpersonating) return;

      dispatch(updateTourProgress({ tourKey, stepReached: step }));

      try {
        await tourAPI.updateProgress(tourKey, { stepReached: step });
      } catch {
        // Silently fail for step updates
      }
    },
    [tourKey, userId, teamId, dispatch, isImpersonating]
  );

  return {
    // State
    isCompleted,
    isSkipped,
    isRunning: isRunning && currentTour === tourKey,
    shouldAutoStart,
    hasNewSteps,
    stepReached,
    discriminators,

    // Actions
    startTour,
    completeTour,
    skipTour,
    resetTour,
    stopTour,
    updateStepReached,
    onClose,
  };
};

export default useTour;

// Hook for auto-starting tours
export const useTourAutoStart = (tourConfig) => {
  const {
    shouldAutoStart,
    startTour,
    stopTour,
    completeTour,
    isCompleted,
    hasNewSteps,
    stepReached,
    onClose,
  } = useTour(tourConfig);
  const { resetCounter, isRunning } = useSelector((state) => state.tour);
  const tourKey = tourConfig?.tourKey;
  const hasStarted = useRef(false);
  const lastResetCounter = useRef(resetCounter);
  const lastTourKey = useRef(tourKey);

  useEffect(() => {
    if (resetCounter !== lastResetCounter.current) {
      hasStarted.current = false;
      lastResetCounter.current = resetCounter;
    }
    if (tourKey !== lastTourKey.current) {
      if (isRunning) {
        stopTour();
      }
      hasStarted.current = false;
      lastTourKey.current = tourKey;
    }
  }, [resetCounter, tourKey, isRunning, stopTour]);

  useEffect(() => {
    if (shouldAutoStart && !hasStarted.current) {
      hasStarted.current = true;
      // Small delay to ensure DOM is ready
      const timeout = setTimeout(() => {
        startTour(completeTour, stepReached, onClose);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [
    shouldAutoStart,
    startTour,
    completeTour,
    isCompleted,
    hasNewSteps,
    stepReached,
    onClose,
    resetCounter,
    tourKey,
  ]);
};
